import React from 'react';
import { User, Shield, TrendingUp, AlertTriangle, Target } from 'lucide-react';

const SettingsView = ({ investorProfile, setInvestorProfile, onExportData, onImportData }) => {
  const profiles = [
    {
      id: 'conservative',
      name: 'Conservative Investor',
      icon: <Shield size={24} />,
      description: 'Focus on capital preservation with low risk tolerance. Suitable for risk-averse investors.',
      characteristics: [
        'Low volatility tolerance',
        'Preference for stable assets',
        'Long-term horizon',
        'Capital preservation priority'
      ],
      recommendedAllocation: {
        'Liquidity': 30,
        'Time Deposit': 40,
        'FCI': 20,
        'ETF': 10,
        'Stock': 0
      },
      expectedReturn: '4-6% APY'
    },
    {
      id: 'moderate',
      name: 'Moderate Investor',
      icon: <Target size={24} />,
      description: 'Balance between growth and risk management. Suitable for most investors.',
      characteristics: [
        'Moderate risk tolerance',
        'Balanced asset allocation',
        'Medium-term horizon',
        'Growth with stability'
      ],
      recommendedAllocation: {
        'ETF': 40,
        'Stock': 25,
        'FCI': 20,
        'Time Deposit': 10,
        'Liquidity': 5
      },
      expectedReturn: '6-8% APY'
    },
    {
      id: 'aggressive',
      name: 'Aggressive Investor',
      icon: <TrendingUp size={24} />,
      description: 'Focus on growth with higher risk tolerance. Suitable for experienced investors.',
      characteristics: [
        'High risk tolerance',
        'Growth-oriented assets',
        'Long-term horizon',
        'Capital growth priority'
      ],
      recommendedAllocation: {
        'Stock': 50,
        'ETF': 30,
        'FCI': 10,
        'Time Deposit': 5,
        'Liquidity': 5
      },
      expectedReturn: '8-12% APY'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-[#041627] text-white rounded-xl">
          <User size={24} />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#041627] font-headline">Investor Profile Settings</h1>
          <p className="text-sm text-slate-500">Configure your investment preferences and risk tolerance</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {profiles.map((profile) => (
          <div
            key={profile.id}
            onClick={() => setInvestorProfile(profile.id)}
            className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
              investorProfile === profile.id
                ? 'border-[#041627] bg-[#041627]/5 shadow-lg'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
            }`}
          >
            {/* Selected indicator */}
            {investorProfile === profile.id && (
              <div className="absolute top-4 right-4 w-6 h-6 bg-[#041627] rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            )}

            <div className="flex items-center gap-4 mb-4">
              <div className={`p-3 rounded-xl ${
                investorProfile === profile.id ? 'bg-[#041627] text-white' : 'bg-slate-100 text-[#041627]'
              }`}>
                {profile.icon}
              </div>
              <div>
                <h3 className="font-bold text-lg text-[#041627]">{profile.name}</h3>
                <p className="text-sm text-slate-500">{profile.expectedReturn}</p>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-4 leading-relaxed">{profile.description}</p>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-[#041627]">Characteristics:</h4>
              <ul className="space-y-1">
                {profile.characteristics.map((char, index) => (
                  <li key={index} className="text-xs text-slate-500 flex items-center gap-2">
                    <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                    {char}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100">
              <h4 className="font-semibold text-sm text-[#041627] mb-3">Recommended Allocation:</h4>
              <div className="space-y-2">
                {Object.entries(profile.recommendedAllocation).map(([asset, percentage]) => (
                  <div key={asset} className="flex justify-between text-xs">
                    <span className="text-slate-600">{asset}</span>
                    <span className="font-semibold text-[#041627]">{percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Current Profile Summary */}
      <div className="bg-gradient-to-r from-[#041627] to-[#1a2b3c] p-8 rounded-2xl text-white">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white/10 rounded-xl">
            <User size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold">Current Profile: {profiles.find(p => p.id === investorProfile)?.name}</h3>
            <p className="text-sm text-slate-300">Your strategy recommendations will be tailored to this profile</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 p-4 rounded-xl">
            <p className="text-xs uppercase opacity-70 mb-1">Risk Tolerance</p>
            <p className="font-bold">
              {investorProfile === 'conservative' ? 'Low' :
               investorProfile === 'moderate' ? 'Medium' : 'High'}
            </p>
          </div>
          <div className="bg-white/5 p-4 rounded-xl">
            <p className="text-xs uppercase opacity-70 mb-1">Expected Return</p>
            <p className="font-bold">{profiles.find(p => p.id === investorProfile)?.expectedReturn}</p>
          </div>
          <div className="bg-white/5 p-4 rounded-xl">
            <p className="text-xs uppercase opacity-70 mb-1">Primary Focus</p>
            <p className="font-bold">
              {investorProfile === 'conservative' ? 'Preservation' :
               investorProfile === 'moderate' ? 'Balance' : 'Growth'}
            </p>
          </div>
          <div className="bg-white/5 p-4 rounded-xl">
            <p className="text-xs uppercase opacity-70 mb-1">Time Horizon</p>
            <p className="font-bold">
              {investorProfile === 'conservative' ? 'Long-term' :
               investorProfile === 'moderate' ? 'Medium-term' : 'Long-term'}
            </p>
          </div>
        </div>
      </div>

      {/* Data Management Section */}
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-[#041627] text-white rounded-xl">
            <Target size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#041627] font-headline">Data Management</h3>
            <p className="text-sm text-slate-500">Backup and restore your portfolio data</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-[#041627]">Export Data</h4>
            <p className="text-sm text-slate-600">
              Download all your portfolio data including operations, goals, and settings as a JSON file for backup purposes.
            </p>
            <button
              onClick={onExportData}
              className="w-full bg-[#041627] text-white py-3 px-6 rounded-lg font-bold hover:opacity-90 transition-colors flex items-center justify-center gap-2"
            >
              <Target size={16} />
              Export Portfolio Data
            </button>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-[#041627]">Import Data</h4>
            <p className="text-sm text-slate-600">
              Restore your portfolio data from a previously exported JSON file. This will replace all current data.
            </p>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={onImportData}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id="import-file"
              />
              <label
                htmlFor="import-file"
                className="w-full bg-slate-100 text-slate-700 py-3 px-6 rounded-lg font-bold hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <Target size={16} />
                Choose JSON File
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h5 className="font-semibold text-sm text-[#041627] mb-2">What gets saved?</h5>
          <ul className="text-xs text-slate-600 space-y-1">
            <li>• Investment operations (buy/sell transactions)</li>
            <li>• Current asset prices</li>
            <li>• Financial goals and targets</li>
            <li>• Investor profile settings</li>
            <li>• Simulation parameters</li>
            <li>• Currency preferences</li>
          </ul>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800 mb-2">Important Note</h4>
            <p className="text-sm text-amber-700">
              Your investor profile affects strategy recommendations and risk assessments.
              Make sure to select the profile that best matches your risk tolerance and investment goals.
              You can change this setting at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;